import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_WECLAPP_PATH } from '../../constants.ts';
import { GET_WECLAPP_INTEGRATIONS, UPDATE_WECLAPP_INTEGRATION } from '../../queries.ts';

const FORM_DEFAULT_VALUES = {
    name: '',
    apiKey: '',
    tenant: '',
    workistTicketCategoryId: '',
    workistImportTicketStatusIds: '',
    workistInProgressTicketStatusId: '',
    workistFinishedTicketStatusId: '',
    workistFinishedTicketCategoryId: '',
    orderChannelIds: [],
};

const WeclappIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('weclapp.title')}`;

    const { data, refetch } = useQuery(GET_WECLAPP_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.weclappIntegrations?.find((i) => i.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                tenant: integration.tenant,
                workistTicketCategoryId: integration.workistTicketCategoryId,
                workistImportTicketStatusIds: integration.workistImportTicketStatusIds
                    .replaceAll(/\[/gi, '')
                    .replaceAll(/\]/gi, '')
                    .replaceAll(/"/gi, ''),
                workistInProgressTicketStatusId: integration.workistInProgressTicketStatusId,
                workistFinishedTicketStatusId: integration.workistFinishedTicketStatusId,
                orderChannelIds: integration.orderChannels.map((c) => c.id),
            });
        }
    }, [integration]);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_WECLAPP_INTEGRATION);

    const handleSubmit = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    apiKey: formData.apiKey,
                    tenant: formData.tenant,
                    workistTicketCategoryId: formData.workistTicketCategoryId,
                    workistImportTicketStatusIds: formData.workistImportTicketStatusIds,
                    workistInProgressTicketStatusId: formData.workistInProgressTicketStatusId,
                    workistFinishedTicketStatusId: formData.workistFinishedTicketStatusId,
                    workistFinishedTicketCategoryId: formData.workistFinishedTicketCategoryId,
                    orderChannels: formData.orderChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('weclapp.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_WECLAPP_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('weclapp.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('weclapp.title'), to: url(CUSTOMER_INTEGRATION_WECLAPP_PATH) },
                            { label: integration?.name || t('weclapp.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard onSubmit={form.handleSubmit(handleSubmit)} title={t('weclapp.form.updateTitle')}>
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('weclapp.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="apiKey"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('weclapp.form.apiKey')}
                                    error={formErrors.apiKey}
                                    helpText={t('secretUpdateOnly')}
                                >
                                    <SecretField
                                        {...field}
                                        secretName="weclappApiKey"
                                        secretId={integration?.id}
                                        defaultIsVisible
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="tenant"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('weclapp.form.tenant')} error={formErrors.tenant}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="workistTicketCategoryId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('weclapp.form.workistTicketCategoryId')}
                                    error={formErrors.workistTicketCategoryId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="workistImportTicketStatusIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('weclapp.form.workistImportTicketStatusIds')}
                                    error={formErrors.workistImportTicketStatusIds}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="workistInProgressTicketStatusId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('weclapp.form.workistInProgressTicketStatusId')}
                                    error={formErrors.workistInProgressTicketStatusId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="workistFinishedTicketStatusId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('weclapp.form.workistFinishedTicketStatusId')}
                                    error={formErrors.workistFinishedTicketStatusId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="workistFinishedTicketCategoryId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('weclapp.form.workistFinishedTicketCategoryId')}
                                    error={formErrors.workistFinishedTicketCategoryId}
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('weclapp.form.orderChannelIds')} error={formErrors.orderChannelIds}>
                                    <CheckboxGroup
                                        {...field}
                                        options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_WECLAPP_PATH)}>
                                    {t('weclapp.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default WeclappIntegrationEditPage;
